/**
 * The ticket queue.
 *
 * A ticket sits in exactly one working queue; the chips are the queue, not a filter over a
 * single list, so the counts and the badge always agree. No email or phone column: the list
 * shows name and ref, and contact lives in the workspace behind a reveal.
 */
import { useState } from 'react';
import type { SupportQueue, TicketListRow } from '@elutide/shared';
import { Card, Chip, Column, DataTable, Pill, Skeleton } from '@/features/customers/temp-ui';
import { useQueueCounts, useTickets } from './api';
import { QUEUE_LABELS } from './types';
import { SLA_TONE, slaLabel } from './slaPill';
import { BulkBar } from './BulkBar';

const QUEUE_ORDER: SupportQueue[] = [
  'needs_reply',
  'waiting_on_customer',
  'live_handoff',
  'unassigned',
  'resolved_7d',
  'all',
];

export function TicketQueue({ onOpen }: { onOpen: (ref: string) => void }) {
  const [queue, setQueue] = useState<SupportQueue>('needs_reply');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const counts = useQueueCounts();
  const tickets = useTickets(queue, query ? { q: query } : {}, null);

  const rows = tickets.data?.items ?? [];

  const columns: Column<TicketListRow>[] = [
    {
      id: 'ref',
      header: 'Ref · subject',
      priority: 1,
      render: (row) => (
        <span>
          <span data-mono="true">{row.public_ref}</span>
          <br />
          <span style={{ color: 'var(--text-muted)' }}>{row.subject}</span>
          {row.order_id ? <span style={{ color: 'var(--text-faint)' }}> · #{row.order_id}</span> : null}
        </span>
      ),
    },
    { id: 'customer', header: 'Customer', priority: 1, render: (row) => row.customer_display },
    {
      id: 'category',
      header: 'Category',
      priority: 2,
      render: (row) => (row.category ? <Pill tone={row.category === 'damaged' ? 'warn' : 'neutral'}>{row.category}</Pill> : null),
    },
    {
      id: 'sla',
      header: 'SLA',
      priority: 1,
      render: (row) => <Pill tone={SLA_TONE[row.sla_state]}>{slaLabel(row)}</Pill>,
    },
    {
      id: 'assignee',
      header: 'Assignee',
      priority: 2,
      render: (row) =>
        row.assignee_name ?? <span style={{ color: 'var(--text-faint)' }}>Unassigned</span>,
    },
  ];

  return (
    <Card
      title="Support"
      action={
        <input
          type="search"
          aria-label="Search tickets"
          placeholder="ref, email, name, order #"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          style={{
            padding: 'var(--space-2)',
            minWidth: 240,
            borderRadius: 'var(--radius-input)',
            border: 'var(--border-width) solid var(--border)',
            background: 'var(--surface-2)',
            color: 'var(--text)',
          }}
        />
      }
    >
      <div
        data-allow-hscroll
        style={{ display: 'flex', gap: 'var(--space-2)', overflowX: 'auto', marginBottom: 'var(--space-3)' }}
      >
        {QUEUE_ORDER.map((id) => (
          <Chip
            key={id}
            label={QUEUE_LABELS[id]}
            active={queue === id}
            count={counts.data ? counts.data[id] : undefined}
            onClick={() => {
              setQueue(id);
              setSelected(new Set());
            }}
          />
        ))}
      </div>

      {counts.data?.handoff_open ? (
        <p role="alert" style={{ background: 'var(--crit-bg)', color: 'var(--crit)', padding: 'var(--space-2)' }}>
          A live chat is waiting. <a href="/support/handoffs">Open</a>
        </p>
      ) : null}

      {tickets.isLoading ? (
        <Skeleton shape="text" lines={6} />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => String(row.ticket_id)}
          onRowClick={(row) => onOpen(row.public_ref)}
          selection={{
            selected,
            onToggle: (key) => {
              const next = new Set(selected);
              if (next.has(key)) next.delete(key);
              else next.add(key);
              setSelected(next);
            },
          }}
          emptyState="Nothing in this queue."
        />
      )}

      <BulkBar selected={[...selected].map(Number)} onDone={() => setSelected(new Set())} />
    </Card>
  );
}
